<template>
  <div class="layout-wrapper">
  <div class="layout-main-container">
    <div class="layout-main" dir="rtl">
      <div class="grid">
        <div class="col-12 mb-2 lg:col-8 lg:mb-0">
          <div class="m-0 flex-grow-1">
            <div class="flex align-items-center gap-2 mb-1 text-blue-500">
              <i :class="['pi', 'pi-file-edit']" style="font-size: 1.25rem;" />
              <label for="borrowingMessage">{{ $t('BORROWING_MESSAGE') }}</label>
            </div>
            <Textarea id="borrowingMessage" rows="3" cols="38" v-model="borrowingMessage" class="w-full" />
          </div>
        </div>
        <div class="col-12 mb-2 lg:col-4 lg:mb-0">
          <div class="flex flex-column align-items-center md:flex-row md:align-items-end lg:flex-column gap-2">
            <div class=" w-full">
              <div class="flex align-items-center gap-2 mb-1 text-blue-500">
                <i :class="['pi', 'pi-calendar-plus']" style="font-size: 1.25rem;" />
                <label for="expirationDate">{{ $t('BORROWING_EXPIRATION_DATE') }}</label>
              </div>
              <Calendar id="expirationDate" v-model="expirationDate" :minDate="minExpirationDate" class="w-full" />
            </div>
            <div class="flex gap-2 w-full">
              <Button :label="$t('CREATE_BORROWING_BUTTON')" :disabled="!canCreateBorrowingRequest"
                @click="createBorrowingRequest" class="w-9" />
              <Button @click="cleanView" class="w-3 justify-content-center gap-1">
                <i :class="['pi', 'pi-eraser']" />
                <span>{{ $t('CLEAR') }}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <Chip v-for="b in selectedBooks" :label="b.title" :id="b.id" :key="b" :closable="true"
          @remove="handleChipRemove"/>
      </div>
      <div class="flex flex-wrap justify-content-center gap-2 mb-2">
            <BookGrid ref="bookGrid" :books="books" :pageSize="pageSize" :totalBooksCount="totalBooksCount"
              :selectableBooks="true" :canFilterByOwners="true" @bookSelected="bookCheckEvent"
              @bookUnselected="bookUnCheckEvent" @filterTriggered="filterBooks"
              @pageRequested="pageRequest => loadPage(pageRequest.number, pageRequest.filter)"/>
      </div>

    </div>
  </div>
</div>
</template>

<script setup>


import { computed, onErrorCaptured, onMounted, ref } from 'vue'
import { handleError } from '@/helpers/ErrorHandler'
import { useToast } from "primevue/usetoast"
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import BookGrid from '@/components/BookGrid'
import Chip from '@/components/Chip.vue'
import bookService from '@/service/BookService'
import operationService from '@/service/OperationService'
import remove from 'lodash/remove'

const authStore = useAuthStore()
const toast = useToast()
const i18n = useI18n()

const bookGrid = ref(null)

const books = ref([])
const borrowingMessage = ref('')
const expirationDate = ref()
const selectedBooks = ref([])

const pageSize = 20
const totalBooksCount = ref(0)

const minExpirationDate = ref(new Date(new Date().getTime() + 5184000000))

onMounted(async () => {  
  try {
    await loadPage(0)
  } catch (error) {
    handleError(authStore, toast, i18n, error)
  }
})

onErrorCaptured((err, instance, info) => {
  return handleError(authStore, toast, i18n, err, instance, info)
})

const loadPage = async (number, filter) => {
  const response = await bookService.getAvailableBooks(number, pageSize, filter)
  books.value = response.collection
  totalBooksCount.value = response.total
  if (selectedBooks.value.length > 0) {
    const selectedBooksIds = selectedBooks.value.map(b => b.id)
    books.value.filter(b => selectedBooksIds.includes(b.id)).forEach(b => b.selected = true)
  }
}

const canCreateBorrowingRequest = computed(() => {
  return selectedBooks.value.length > 0 && expirationDate.value !== undefined && expirationDate.value !== null
})

const createBorrowingRequest = async () => {
  const createResult = await operationService.createBorrowing(borrowingMessage.value, selectedBooks.value.map(b => b.id), expirationDate.value)
  await cleanView()
  toast.add({ severity: 'info', summary: createResult.message, detail: createResult.message, life: 3000 })
}

const filterBooks = async (filter) => {
  await loadPage(0, filter)
}

const cleanView = async () => {
  bookGrid.value.resetFilter()
  bookGrid.value.resetPagination()
  selectedBooks.value = []
  borrowingMessage.value = null
  expirationDate.value = null
  await loadPage(0)
}

// events

const bookCheckEvent = async (bookId, filter) => {
  const book = books.value.filter(b => b.id === bookId)[0]
  selectedBooks.value.push(book)
  if (!bookGrid.value.isOwnerFixed()) {
    if (filter.ownerId === null || filter.ownerId === undefined || filter.ownerId.length !== 1) {
      filter.ownerId = [book.owner.id]
      await loadPage(0, filter)
      bookGrid.value.resetPagination()
    }
    bookGrid.value.fixOwner(book.owner.id)
  }
}

const bookUnCheckEvent = async bookId => {
  await removeFromSelectedBooks(bookId)
}

const handleChipRemove = async (bookId) => {
  const book = books.value.filter(b => b.id === bookId)[0]
  if (book !== undefined && book !== null) {
    // if in current page
    book.selected = false
  }
  await removeFromSelectedBooks(bookId)
}

const removeFromSelectedBooks = async (bookId) => {
  remove(selectedBooks.value, b => b.id === bookId)
  if (selectedBooks.value.length == 0) {
    bookGrid.value.freeOwner()
    bookGrid.value.resetPagination()
    await loadPage(0, bookGrid.value.getCurrentFilter())
  }
}

</script>

<style scoped>

</style>